import { evidenceOriginInput } from "./document-remittance";
import { CANAIS_ENTREGA, CANAIS_ENVIO } from "./field-options";
import type { FormalizationSummary } from "./formalization";
import { z } from "zod";
import { ACTIVE_SERVICE_TYPES } from "./types";
import type {
  RemontaDossieSinistro, RemontaOrdemServico, RemontaOrdemChecklist,
  RemontaOrdemChecklistItem, RemontaOrdemPendencia, RemontaDossieArquivo,
  RemontaOrcamentoItem, RemontaLaudo,
  remontaDossiePreAnalises,
} from "../schema";

export const initialStudyInput = z.object({
  versao: z.number().int().nonnegative(),
  status: z.enum(["em_estudo", "pendencia", "concluida"]),
  dadosEstudados: z.string().trim().max(15000).default(""),
  documentosConsultados: z.string().trim().max(15000).default(""),
  limitacoes: z.string().trim().max(10000).default(""),
  conclusao: z.string().trim().max(15000).default(""),
  recomendacoes: z.array(z.object({
    tipo: z.enum(ACTIVE_SERVICE_TYPES),
    viabilidade: z.enum(["viavel", "condicionada", "inviavel"]),
    fundamentacao: z.string().trim().max(4000),
  }).strict()).max(4).default([]),
}).strict().superRefine((value, ctx) => {
  if (new Set(value.recomendacoes.map((item) => item.tipo)).size !== value.recomendacoes.length) {
    ctx.addIssue({ code: "custom", path: ["recomendacoes"], message: "Cada serviço deve ter apenas uma recomendação." });
  }
  if (value.status === "concluida" && (value.conclusao.length < 20 || value.dadosEstudados.length < 10)) {
    ctx.addIssue({ code: "custom", path: ["conclusao"], message: "Registre os dados estudados e uma conclusão fundamentada antes de publicar." });
  }
  if (value.status === "concluida" && value.recomendacoes.some((item) => item.fundamentacao.length < 10)) {
    ctx.addIssue({ code: "custom", path: ["recomendacoes"], message: "Fundamente cada recomendação antes de publicar." });
  }
  if (value.status === "pendencia" && value.limitacoes.length < 10) {
    ctx.addIssue({ code: "custom", path: ["limitacoes"], message: "Descreva os documentos ou dados que precisam ser complementados." });
  }
});
export type InitialStudyInput = z.infer<typeof initialStudyInput>;
export type InitialStudy = typeof remontaDossiePreAnalises.$inferSelect;

export const internalItemInput = z.object({
  versao: z.number().int().positive(),
  statusInterno: z.enum(["nao_solicitado", "solicitado", "recebido", "nao_se_aplica"]),
  justificativaInterna: z.string().trim().max(4000).default(""),
  observacaoTecnica: z.string().trim().max(10000).default(""),
  origemEvidencia: evidenceOriginInput.nullable().optional(),
}).strict().superRefine((value, ctx) => {
  if (value.statusInterno === "nao_se_aplica" && value.justificativaInterna.length < 10) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["justificativaInterna"], message: "Explique por que este item não se aplica (mínimo 10 caracteres)." });
  }
});

export const externalItemInput = z.object({
  versao: z.number().int().positive(),
  respostaExterna: z.enum(["encaminhado", "indisponivel", "nao_se_aplica"]),
  justificativaExterna: z.string().trim().max(4000).default(""),
}).strict().superRefine((value, ctx) => {
  if (value.respostaExterna !== "encaminhado" && value.justificativaExterna.length < 10) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["justificativaExterna"], message: "Informe a justificativa (mínimo 10 caracteres)." });
  }
});

export const preAnalysisConclusionInput = z.object({
  versao: z.number().int().positive(),
  resultado: z.enum(["apto_para_execucao", "pendencia_documental", "inviavel_tecnicamente", "fora_do_escopo_contratado"]),
  justificativa: z.string().trim().min(10).max(10000),
  excepcional: z.boolean().default(false),
  confirmacao: z.boolean().optional(),
}).strict();

export const orderDetailsInput = z.object({
  versao: z.number().int().positive(), prazoDocumentosEm: z.string().nullable().optional(), prazoEntregaEm: z.string().nullable().optional(),
  vistoriaPrevistaEm: z.string().nullable().optional(), numeroOrdemExterna: z.string().trim().max(200).nullable().optional(),
  reguladorExterno: z.string().trim().max(200).nullable().optional(), responsavelId: z.string().uuid().optional(),
  canalEntrega: z.enum(CANAIS_ENTREGA).nullable().optional(), canalEnvio: z.enum(CANAIS_ENVIO).nullable().optional(),
}).strict().superRefine((input, ctx) => {
  for (const key of ["prazoDocumentosEm", "prazoEntregaEm", "vistoriaPrevistaEm"] as const) if (input[key] !== undefined && input[key] !== null) {
    const value = input[key]!; const date = new Date(`${value}T12:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0,10) !== value) ctx.addIssue({ code: "custom", path: [key], message: "Data inválida." });
  }
});

export const orderActionInput = z.object({
  versao: z.number().int().positive(),
  acao: z.enum(["solicitar_documentos", "iniciar_pre_analise", "iniciar_execucao", "enviar_revisao", "aprovar_entrega", "devolver_entrega", "cancelar", "retomar"]),
  justificativa: z.string().trim().max(10000).default(""),
}).strict();

export const pendencyInput = z.object({
  descricao: z.string().trim().min(10).max(4000),
  checklistItemId: z.string().uuid().nullable().optional(),
  prazo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((date) => {
    const value = new Date(`${date}T12:00:00Z`);
    return !Number.isNaN(value.getTime()) && value.toISOString().slice(0, 10) === date;
  }, "Data inválida").nullable().optional(),
}).strict();

export const resolvePendencyInput = z.object({ resposta: z.string().trim().min(10).max(4000) }).strict();

const technicalText = z.string().trim().max(15000).default("");
const base = { versao: z.number().int().nonnegative(), metodologia: technicalText, limitacoes: technicalText, conclusao: technicalText };
export const technicalDeliveryInput = z.discriminatedUnion("tipo", [
  z.object({ ...base, tipo: z.literal("nexo_causal"), narrativa: technicalText, dinamica: technicalText, geometria: technicalText, indicativos: technicalText, classificacao: z.enum(["compativel", "incompativel", "inconclusivo"]).nullable().default(null) }).strict(),
  z.object({ ...base, tipo: z.literal("sindicancia"), objetivo: technicalText, diligencias: technicalText, fontes: technicalText, achados: technicalText }).strict(),
  z.object({ ...base, tipo: z.literal("orcamento_tecnico"), objeto: technicalText, criterios: technicalText }).strict(),
]);
export type TechnicalDeliveryInput = z.infer<typeof technicalDeliveryInput>;
export type TechnicalContent = Omit<TechnicalDeliveryInput, "versao">;

export type TechnicalDelivery = {
  id: string;
  ordemServicoId: string;
  conteudo: TechnicalDeliveryInput;
  versao: number;
  autorId: string;
  revisadoPor: string | null;
  revisaoJustificativa: string | null;
  publicadoEm: Date | string | null;
  atualizadoEm: Date | string;
};

export type PreAnalysisSummary = {
  total: number; recebidos: number; naoAplicaveis: number; faltantes: number;
  respostasObrigatoriasFaltantes: number; recebidosSemOrigem: number; essenciaisFaltantes: number;
  documentais: { total: number; respondidos: number; conferidos: number }; internos: { total: number; conferidos: number };
  semJustificativa: number; pendenciasAbertas: number; podeLiberar: boolean;
};

/**
 * Public allowlist for documental replies. Neutral internal fields preserve the
 * current OrderWorkspace/OrderChecklist contract; they never describe a review.
 * New persisted fields require an explicit decision before being exposed here.
 */
export type ExternalOrderChecklistItem = Pick<RemontaOrdemChecklistItem,
  "id" | "checklistId" | "codigo" | "codigoOrigem" | "numero" | "secao" | "categoria" |
  "descricao" | "orientacao" | "obrigatorio" | "respostaExterna" | "justificativaExterna" |
  "respondidoPor" | "respondidoEm" | "versao" | "criadoEm" | "atualizadoEm"
> & {
  statusInterno: "nao_solicitado";
  justificativaInterna: null;
  observacaoTecnica: null;
  analisadoPor: null;
  analisadoEm: null;
};

export type WorkspaceChecklistItem = Omit<RemontaOrdemChecklistItem, "origemEvidencia" | "politicaItem"> & Partial<Pick<RemontaOrdemChecklistItem, "origemEvidencia" | "politicaItem">>;

export type OrderWorkspace = {
  ordem: RemontaOrdemServico;
  dossie: RemontaDossieSinistro;
  interno: boolean;
  podeAnalisar: boolean;
  podeExecutar: boolean;
  podeRevisar: boolean;
  checklists: Array<RemontaOrdemChecklist & { itens: WorkspaceChecklistItem[] }>;
  pendencias: RemontaOrdemPendencia[];
  arquivos: RemontaDossieArquivo[];
  portalArquivos?: Array<import('../documentos-externos').PortalFileView & {coletaId:string;acessoEmail:string}>;
  entrega: TechnicalDelivery | null;
  laudos: RemontaLaudo[];
  orcamento: RemontaOrcamentoItem[];
  resumo: PreAnalysisSummary | null;
  formalizacao?: FormalizationSummary;
  remessas?: Array<typeof import("../schema").remontaDocumentRemittances.$inferSelect>;
};

export type ServiceQueueRow = RemontaOrdemServico & {
  numeroSinistro: string | null;
  seguradoNome: string | null;
  objetoTipo: string | null;
};

export const ORDER_STATUS_LABELS: Record<string, string> = {
  rascunho: "Rascunho", solicitado: "Solicitado", aguardando_documentos: "Aguardando documentos",
  em_pre_analise: "Em conferência", pendencia: "Pendência", apto_para_execucao: "Apto para execução",
  em_execucao: "Em execução", em_revisao: "Em revisão", concluido: "Concluído", cancelado: "Cancelado",
};

export const RESULT_LABELS: Record<string, string> = {
  apto_para_execucao: "Apto para execução", pendencia_documental: "Pendência documental",
  inviavel_tecnicamente: "Inviável tecnicamente", fora_do_escopo_contratado: "Fora do escopo contratado",
};
